import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { ArrowRight, PenTool, Magnet, BarChart3, Eye } from "lucide-react";
import { AnimatedText } from "@/components/ui/animated-hero";

const VIDEO_DESKTOP = "https://cpnxwtiyemsrigrtetmd.supabase.co/storage/v1/object/public/assets/video/animacja_viraluj_small.mp4";
const VIDEO_MOBILE = "https://cpnxwtiyemsrigrtetmd.supabase.co/storage/v1/object/public/assets/video/viraluj_tworz_content.mp4";
const POSTER_DESKTOP = "https://cpnxwtiyemsrigrtetmd.supabase.co/storage/v1/object/public/assets/video/video_thumbnail.jpg";
const POSTER_MOBILE = "https://cpnxwtiyemsrigrtetmd.supabase.co/storage/v1/object/public/assets/video/viralisek_thumbnail_tworz_viralowe_video.png.jpg";

const MOBILE_BREAKPOINT = 768;

const HERO_WORDS = [
  "piszemy scenariusze",
  "sprawdzamy fakty",
  "śledzimy konkurencję",
  "szukamy trendów",
  "tworzymy hooki",
  "robimy research",
  "pilnujemy pomysłów",
];

const MODULES = [
  { icon: Magnet, label: "Magnes Pomysłów", shortLabel: "Magnes" },
  { icon: BarChart3, label: "Analityka konta", shortLabel: "Analityka" },
  { icon: PenTool, label: "Kreator Scenariusza", shortLabel: "Kreator" },
  { icon: Eye, label: "Śledzenie konkurencji", shortLabel: "Konkurencja" },
] as const;

const HeroSection = () => {
  const navigate = useNavigate();
  const [videoReady, setVideoReady] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < MOBILE_BREAKPOINT);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleCanPlay = useCallback(() => {
    setVideoReady(true);
  }, []);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener("resize", handleResize, { passive: true });
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const videoUrl = isMobile ? VIDEO_MOBILE : VIDEO_DESKTOP;
  const posterUrl = isMobile ? POSTER_MOBILE : POSTER_DESKTOP;

  // Reset video state when source changes
  useEffect(() => {
    setVideoReady(false);
  }, [videoUrl]);

  // Play video once visible — retries on failure (iOS needs user-visible element)
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    let retryTimer: ReturnType<typeof setTimeout>;

    const tryPlay = () => {
      video.play().catch(() => {
        retryTimer = setTimeout(tryPlay, 500);
      });
    };

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          tryPlay();
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(video);

    return () => {
      observer.disconnect();
      clearTimeout(retryTimer);
    };
  }, [videoUrl]);

  return (
    <section className="relative min-h-[100dvh] flex items-center justify-center overflow-hidden py-12 md:py-24">
      {/* Poster fallback as background image */}
      <div
        className="absolute inset-0 bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url(${posterUrl})` }}
      />

      {/* Video background with fade-in — no autoPlay, starts via IntersectionObserver */}
      <video
        key={videoUrl}
        ref={videoRef}
        muted
        loop
        playsInline
        poster={posterUrl}
        preload="auto"
        onCanPlay={handleCanPlay}
        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
        style={{ opacity: videoReady ? 1 : 0 }}
      >
        <source src={videoUrl} type="video/mp4" />
      </video>

      {/* Darker gradient overlay — supports readable text without glassmorphism box */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/65 to-black/80" />

      {/* Subtle radial vignette for cinematic depth */}
      <div
        className="absolute inset-0"
        style={{
          background: "radial-gradient(ellipse at center, transparent 45%, rgba(0, 0, 0, 0.35) 100%)",
        }}
      />

      {/* Centered content — glass card for legibility on busy video bg.
          mt-16 on mobile only: shifts the card below center so the
          background poster/video has breathing room above. */}
      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 mt-40 sm:mt-0">
        <div
          className="text-center max-w-4xl mx-auto p-5 sm:p-10 md:p-12 rounded-3xl"
          style={{
            background: "linear-gradient(135deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
            backdropFilter: "blur(20px) saturate(150%)",
            WebkitBackdropFilter: "blur(20px) saturate(150%)",
            border: "1px solid rgba(255, 255, 255, 0.12)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          }}
        >
          {/* H1 — tight 0.96 line-height per Superhuman display rule */}
          <h1
            className="text-[28px] sm:text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-white"
            style={{ lineHeight: 0.96 }}
          >
            <span className="block">Ty rośniesz<span className="text-primary">.</span></span>
            <span className="block mt-2 md:mt-3 whitespace-nowrap">
              My <AnimatedText words={HERO_WORDS} className="text-primary" />
            </span>
          </h1>

          <div className="mt-6 md:mt-10 max-w-3xl mx-auto text-base md:text-lg text-white/90 leading-[1.5]">
            <p>
              Viraluj to cały warsztat do tworzenia filmów na Instagram.
            </p>
            <p className="text-white/75">
              Bez blokady twórczej. Nagrywaj, rozwijaj swój profil, zdobywaj klientów i obserwatorów.
            </p>
          </div>

          {/* CTA + mikrocopy — full-width pill on mobile (large tap target), auto-width on desktop */}
          <div className="mt-8 md:mt-10 flex flex-col items-center gap-3">
            <Button
              size="lg"
              onClick={() => navigate("/auth?mode=register&trial=true")}
              className="group relative w-full sm:w-auto px-8 py-7 md:py-6 text-base font-semibold bg-primary text-white hover:bg-primary/90 rounded-full transition-all shadow-lg shadow-primary/25 overflow-hidden active:scale-[0.98]"
            >
              {/* Shimmer overlay */}
              <span
                className="absolute inset-0 pointer-events-none"
                style={{
                  background: "linear-gradient(120deg, transparent 35%, rgba(255,255,255,0.15) 50%, transparent 65%)",
                  backgroundSize: "200% 100%",
                  animation: "shimmer-sweep 4s ease-in-out infinite",
                }}
              />
              <span className="relative z-10 flex items-center">
                <span className="sm:hidden">Wypróbuj za darmo</span>
                <span className="hidden sm:inline">Wypróbuj przez 5 dni za darmo</span>
                <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Button>

            <button
              onClick={() => navigate("/konsultacja")}
              className="mt-1 text-sm text-white/60 hover:text-white/90 underline underline-offset-2 transition-colors duration-200"
            >
              Nie jesteś pewny? Umów bezpłatną analizę konta →
            </button>
          </div>

          {/* Module strip — kills "ChatGPT wrapper" misconception by showing platform breadth. Short labels on mobile to fit one line on most viewports. */}
          <div className="mt-8 md:mt-12 flex flex-wrap items-center justify-center gap-x-3 sm:gap-x-5 gap-y-2 text-xs sm:text-sm text-white/80">
            {MODULES.map(({ icon: Icon, label, shortLabel }, i) => (
              <div key={label} className="flex items-center gap-x-1.5 sm:gap-x-2">
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-primary/90" strokeWidth={2} />
                <span className="sm:hidden">{shortLabel}</span>
                <span className="hidden sm:inline">{label}</span>
                {i < MODULES.length - 1 && (
                  <span className="hidden sm:inline ml-3 text-white/30" aria-hidden="true">
                    ·
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CSS Animation styles */}
      <style>{`
        @keyframes bounce-gentle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-8px); }
        }
        .animate-bounce-gentle {
          animation: bounce-gentle 2s ease-in-out infinite;
        }
        @keyframes shimmer-sweep {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `}</style>
    </section>
  );
};

export default HeroSection;
